import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export type ContentType =
  | 'image' | 'video' | 'video-stream'
  | 'audio' | 'audio-stream' | 'model'
  | 'text' | 'rich-text' | 'list';

export interface ContentFile {
  id: string;
  language: string;
  mimeType: string;
  fileSize: number;
  version: number;
  contentId: string;
  createdDate: string;
  updatedDate: string;
}

export interface Content {
  id: string;
  title: string;
  description?: string;
  type: ContentType;
  spaceId: string | null;
  files?: ContentFile[];
  createdAt: string;
  updatedAt: string;
}

export interface ListContentsOptions {
  includeAncestors?: boolean;
  includePlaceholderLinks?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateContentDto {
  title: string;
  type: ContentType;
  description?: string;
  spaceId: string;
}

export interface UpdateContentDto {
  title?: string;
  description?: string;
}

/**
 * Portal CMS content state — list and detail views over
 * `/api/ecms/contents`. All reads are space-scoped (server-side
 * `findContentsInSpace`); writes carry a `File` via multipart. The
 * service exposes per-language helpers (`filesByLanguage`,
 * `latestPerLanguage`) so detail pages can show one row per locale with
 * the active version highlighted.
 */
@Injectable({ providedIn: 'root' })
export class ContentService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _contents = signal<Content[]>([]);
  private readonly _selectedContent = signal<Content | null>(null);
  private readonly _listLoading = signal(false);
  private readonly _detailLoading = signal(false);

  readonly contents = this._contents.asReadonly();
  readonly selectedContent = this._selectedContent.asReadonly();
  readonly loading = this._listLoading.asReadonly();
  readonly detailLoading = this._detailLoading.asReadonly();

  /**
   * Map `language → latest ContentFile` for the currently-selected content.
   * Newer versions shadow older ones; the detail UI shows one tab per language.
   */
  readonly latestPerLanguage = computed<Map<string, ContentFile>>(() => {
    const content = this._selectedContent();
    const result = new Map<string, ContentFile>();
    if (!content?.files) return result;
    for (const file of content.files) {
      const existing = result.get(file.language);
      if (!existing || file.version > existing.version) {
        result.set(file.language, file);
      }
    }
    return result;
  });

  /**
   * Set of language codes present on the currently-selected content.
   * Driven from `latestPerLanguage` so the order matches what the detail
   * page renders. `all` is the system default and always sorts first.
   */
  readonly languages = computed<string[]>(() => {
    const keys = Array.from(this.latestPerLanguage().keys());
    return keys.sort((a, b) => {
      if (a === 'all') return -1;
      if (b === 'all') return 1;
      return a.localeCompare(b);
    });
  });

  async loadContents(spaceId: string, opts: ListContentsOptions = {}): Promise<void> {
    this._listLoading.set(true);
    try {
      let params = `spaceId=${encodeURIComponent(spaceId)}`;
      if (opts.includeAncestors) params += '&includeAncestors=true';
      if (opts.includePlaceholderLinks) params += '&includePlaceholderLinks=true';
      if (typeof opts.limit === 'number') params += `&limit=${opts.limit}`;
      if (typeof opts.offset === 'number') params += `&offset=${opts.offset}`;
      const data = await lastValueFrom(this.http.get<Content[]>(`${this.api}/ecms/contents?${params}`));
      this._contents.set(data);
    } finally {
      this._listLoading.set(false);
    }
  }

  async loadContent(id: string, includePlaceholderLinks = true): Promise<void> {
    this._detailLoading.set(true);
    try {
      const query = includePlaceholderLinks ? '?includePlaceholderLinks=true' : '';
      const data = await lastValueFrom(
        this.http.get<Content>(`${this.api}/ecms/contents/${id}${query}`),
      );
      this._selectedContent.set(data);
    } finally {
      this._detailLoading.set(false);
    }
  }

  async createContent(dto: CreateContentDto, file: File): Promise<Content> {
    const form = this.toMultipart(dto, file);
    const created = await lastValueFrom(this.http.post<Content>(`${this.api}/ecms/contents`, form));
    this._contents.update((list) => [...list, created]);
    return created;
  }

  async updateContent(id: string, dto: UpdateContentDto, file?: File): Promise<Content> {
    const form = this.toMultipart(dto, file);
    const updated = await lastValueFrom(this.http.patch<Content>(`${this.api}/ecms/contents/${id}`, form));
    this._contents.update((list) => list.map((c) => (c.id === id ? updated : c)));
    if (this._selectedContent()?.id === id) {
      // Re-fetch to get the refreshed files[] — PATCH returns the content
      // row but files may have been versioned on the server.
      await this.loadContent(id);
    }
    return updated;
  }

  async deleteContent(id: string): Promise<void> {
    await lastValueFrom(this.http.delete(`${this.api}/ecms/contents/${id}`));
    this._contents.update((list) => list.filter((c) => c.id !== id));
    if (this._selectedContent()?.id === id) {
      this._selectedContent.set(null);
    }
  }

  /**
   * Upload a new file variant for the given language. Re-uploading an
   * existing language creates a new version row (unique index is on
   * `(contentId, version, language)`) — the SPA shows the latest only.
   */
  async addLanguageVariant(contentId: string, language: string, file: File): Promise<void> {
    const form = new FormData();
    form.append('language', language);
    form.append('file', file, file.name);
    await lastValueFrom(this.http.post(`${this.api}/ecms/contents/${contentId}/files`, form));
    if (this._selectedContent()?.id === contentId) {
      await this.loadContent(contentId);
    }
  }

  clearSelection(): void {
    this._selectedContent.set(null);
  }

  /**
   * Build a multipart form: scalar DTO fields go as plain string parts,
   * the optional file goes under the `file` field name (matches the
   * `FileInterceptor('file')` on the controller).
   */
  private toMultipart(dto: object, file?: File): FormData {
    const form = new FormData();
    for (const [key, value] of Object.entries(dto as Record<string, unknown>)) {
      if (value === undefined || value === null) continue;
      form.append(key, String(value));
    }
    if (file) form.append('file', file, file.name);
    return form;
  }
}
